import React, { useEffect, useRef } from "react";
import styled from "styled-components";

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.48);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    -webkit-tap-highlight-color: transparent;
`;

const Container = styled.div`
    width: 520px;
    max-width: calc(100% - 32px);
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 12px 30px rgba(10, 30, 60, 0.18);
    padding: 22px 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    outline: none;
    font-family: "Poppins", sans-serif;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 18px;
    line-height: 1.2;
    font-weight: 700;
    color: #0f1724;
`;

const Body = styled.div`
    color: #374151;
    font-size: 14px;
`;

const DashList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 6px;
`;

const DashItem = styled.div`
    position: relative;
    padding-left: 20px;
    line-height: 1.45;
    font-size: 15px;
    color: #374151;
    &::before {
        content: "-";
        position: absolute;
        left: 0;
        top: 0;
        font-weight: 700;
        color: #374151;
    }
`;

const ActionsRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 6px;
`;

const BaseButton = styled.button`
    min-width: 92px;
    padding: 8px 14px;
    border-radius: 8px;
    border: 1px solid transparent;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: none;
    transition: transform 0.06s ease, box-shadow 0.06s ease;
    &:active {
        transform: translateY(1px);
    }
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const SecondaryButton = styled(BaseButton)`
    background: #ffffff;
    color: #374151;
    border-color: #d1d5db;
`;

const PrimaryButton = styled(BaseButton)`
    background: #0f9d58;
    color: #ffffff;
    border-color: #0f9d58;
`;

const HiddenClose = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 34px;
    height: 34px;
    border: 0;
    background: transparent;
    cursor: pointer;
    color: transparent;
    &:focus {
        outline: none;
    }
`;

const useLockBodyScroll = locked => {
    useEffect(() => {
        if (!locked) return;
        const origOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = origOverflow;
        };
    }, [locked]);
};

const NoticeModal = ({
    visible = false,
    heading = "Technical Data and CUI/CDI is strictly prohibited",
    onAccept = () => {},
    onCancel = () => {},
    singleOk = false,
    isLoading = false
}) => {
    const acceptRef = useRef(null);
    const cancelRef = useRef(null);

    useLockBodyScroll(visible);

    useEffect(() => {
        if (!visible) return;
        const toFocus = acceptRef.current || cancelRef.current;
        setTimeout(() => {
            if (toFocus && typeof toFocus.focus === "function") toFocus.focus();
        }, 40);

        const handleKey = e => {
            if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
            } else if (e.key === "Enter") {
                e.preventDefault();
                onAccept();
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [visible, onAccept, onCancel]);

    if (!visible) return null;

    return (
        <Overlay role="presentation" aria-hidden={false}>
            <Container role="dialog" aria-modal="true" aria-labelledby="fca-notice-title" tabIndex={-1}>
                <HiddenClose aria-label="Close notice" onClick={onCancel} />

                <Title id="fca-notice-title">{heading}</Title>

                <Body>
                    <DashList>
                        <DashItem>
                            Don't enter any Technical Data or information about a product being manufactured by manufacturing equipment.
                        </DashItem>
                        <DashItem>ONLY enter information about the equipment itself.</DashItem>
                        <DashItem>
                            If you're unsure whether the information you need to share may constitute Technical Data, <strong>STOP</strong> and
                            contact the CBRE Facility Manager before proceeding.
                        </DashItem>
                        <DashItem>Don't use customer name, program name, or room description; refer to building/room numbers only.</DashItem>
                    </DashList>
                </Body>

                {singleOk ? (
                    <ActionsRow>
                        <PrimaryButton onClick={() => onAccept()} ref={acceptRef} disabled={isLoading} aria-label="Ok">
                            Ok
                        </PrimaryButton>
                    </ActionsRow>
                ) : (
                    <ActionsRow>
                        <SecondaryButton onClick={() => onCancel()} ref={cancelRef} disabled={isLoading} aria-label="Cancel">
                            Cancel
                        </SecondaryButton>
                        <PrimaryButton onClick={() => onAccept()} ref={acceptRef} disabled={isLoading} aria-label="Accept">
                            Accept
                        </PrimaryButton>
                    </ActionsRow>
                )}
            </Container>
        </Overlay>
    );
};

export default NoticeModal;
